import {LayeringMode} from 'constants/MapsConstants';
import * as MapsConstants from 'constants/MapsConstants';
import {Strand} from 'constants/MolbioConstants';
import Type from 'molbio/maps/Type';
import {EllipsisDirection, ellipsize} from 'util/NameUtil';

export interface AnnotationRenderParams {
  start: number;
  end: number;
  width: number;
  height: number;
  tipWidth: number;
  arrowsOffset: number;
  arrowWidth: number;
  arrowSpacing: number;
  color: string;
  labelColor: string;
  outsideLabelColor: string;
  forceOpacity?: number;
  forceName?: string;
  outOfSync?: boolean;
}

export interface AnnotationsPathParams {
  /** The d attribute for the fill path of an annotation view */
  fillD: string;
  /** The d attribute for the left hand side stroke path of an annotation view */
  strokeLeftD: string;
  /** The d attribute for the top and bottom stroke paths of an annotation view */
  strokeMiddleD: string;
  /** The d attribute for the right hand side path of an annotation view */
  strokeRightD: string;
  /** The shape-rendering attribute for the left hand side path of an annotation view */
  strokeLeftRender: string;
  /** The shape-rendering attribute for the right hand side path of an annotation view */
  strokeRightRender: string;
  /** The shape-rendering attribute for the top and bottom paths of an annotation view */
  strokeMiddleRender: string;
}

/**
 * Gets the d and shape-rendering attributes for an annotation
 * @param params Information about how to render an annotation
 * @param sliceStart The index at which the slice starts
 * @param sliceEnd The index at which the slice ends
 * @param strand Which strand the annotation is on
 */
export function getAnnotationPathParams(
  params: AnnotationRenderParams,
  sliceStart: number,
  sliceEnd: number,
  strand: Strand
): AnnotationsPathParams {
  let fillD: string = '';
  let strokeLeftD: string = '';
  let strokeTopD: string = '';
  let strokeBottomD: string = '';
  let strokeRightD: string = '';
  let strokeLeftRender: string = 'crispEdges';
  let strokeRightRender: string = 'crispEdges';
  const strokeMiddleRender: string = 'crispEdges';
  const {start, end, width, height, tipWidth} = params;

  const jagWidth = 4;
  const jagHeight = height / 4;

  const showLeftEnd = start >= sliceStart;
  const showRightEnd = end <= sliceEnd;

  if (showLeftEnd) {
    if (strand === -1) {
      // show left tip
      strokeLeftD += `M ${tipWidth} ${height} L 0 ${height / 2} L ${tipWidth} 0 `;
      strokeTopD += `M ${tipWidth} 0 `;
      strokeBottomD += `M ${tipWidth} ${height} `;
      fillD += strokeLeftD;
      strokeLeftRender = 'geometricPrecision';
    } else {
      // show line
      strokeLeftD += `M 0 ${height} L 0 0 `;
      strokeTopD += 'M 0 0 ';
      strokeBottomD += `M 0 ${height} `;
      fillD += strokeLeftD;
    }
  } else {
    // show jagged line
    strokeLeftD += `M 0 ${height} L ${jagWidth} ${height - jagHeight} L 0 ${
      height - jagHeight * 2
    } L ${jagWidth} ${height - jagHeight * 3 + 1} L 0 0 `;
    strokeTopD += 'M 0 0 ';
    strokeBottomD += `M 0 ${height} `;
    fillD += strokeLeftD;
    strokeLeftRender = 'geometricPrecision';
  }

  if (showRightEnd) {
    if (strand === 1) {
      // show right tip
      strokeRightD += `M ${width - tipWidth} 0 L ${width} ${height / 2} L ${width - tipWidth} ${height} `;
      strokeTopD += `L ${width - tipWidth} 0 `;
      strokeBottomD += `L ${width - tipWidth} ${height} `;
      fillD += `L ${width - tipWidth} 0 L ${width} ${height / 2} L ${width - tipWidth} ${height} `;
      strokeRightRender = 'geometricPrecision';
    } else {
      // show line
      strokeRightD += `M ${width} 0 L ${width} ${height} `;
      strokeTopD += `L ${width} 0 `;
      strokeBottomD += `L ${width} ${height} `;
      fillD += `L ${width} 0 L ${width} ${height} `;
    }
  } else {
    // show jagged line
    // strokeRightD += "M #{width} 0 L #{width} #{height} "
    strokeRightD += `M ${width} 0 L ${width - jagWidth} ${height - jagHeight * 3 + 1} L ${width} ${
      height - jagHeight * 2
    } L ${width - jagWidth} ${height - jagHeight} L ${width} ${height} `;
    strokeTopD += `L ${width} 0 `;
    strokeBottomD += `L ${width} ${height} `;
    fillD += `L ${width} 0 L ${width - jagWidth} ${height - jagHeight * 3 + 1} L ${width} ${
      height - jagHeight * 2
    } L ${width - jagWidth} ${height - jagHeight} L ${width} ${height} `;
    strokeRightRender = 'geometricPrecision';
  }

  return {
    fillD,
    strokeLeftD,
    strokeRightD,
    strokeMiddleD: `${strokeTopD}${strokeBottomD}`,
    strokeLeftRender,
    strokeRightRender,
    strokeMiddleRender,
  };
}

/**
 * Returns a list of x pixel positions where labels should be drawn for an annotation relative to the
 * location of the annotation, given width constraints of the text and the visible slice.
 *
 * @param labelWidth The width in pixels of the text of the label
 * @param tipWidth The width in pixels of the pointed tip of the annotation
 * @param width The width in pixels of the annotation
 * @param strand The strand on which the annotation lies
 * @param visibleWidth The width in pixels of the slice
 * @param residuesType The Type object of the residues slice
 * @param leftBP The residue index of the LHS of the viewport on the minimap, if we're on the minimap
 * @param rightBP The residue index of the RHS of the viewport on the minimap, if we're on the minimap
 * @param annotationStart The residue index of the annotation start position
 */
export function getLabelLocations(
  labelWidth: number,
  tipWidth: number,
  width: number,
  strand: number,
  visibleWidth: number,
  residuesType: Type,
  leftBP: number | null,
  rightBP: number | null,
  annotationStart: number
): Array<number> {
  let x;
  // To prevent infinite loops in the case visibleWidth <= 0 for no good reason.
  // And to prevent labels from overlapping if the text is very long.
  const visWidth = Math.max(labelWidth + 50, Math.max(visibleWidth, 100));
  const labelData = [];

  if (width < visWidth) {
    x = (width - tipWidth - labelWidth) / 2;
    if (strand === -1) {
      x += tipWidth;
    }
    labelData.push(x);
  } else {
    // Put the first label at the middle of the visible annotation portion
    x = (visWidth - tipWidth - labelWidth) / 2;
    if (strand === -1) {
      x += tipWidth;
    }

    let max = width - tipWidth;
    const annotationStartX = residuesType.width(annotationStart);

    // When on the minimap, adjust for the currently visible slice portion
    if (leftBP != null && rightBP != null) {
      x += Math.max(0, Math.floor((residuesType.width(leftBP) - annotationStartX - x) / visWidth) * visWidth);
      max = Math.min(residuesType.width(rightBP) - annotationStartX, max);
    }
    // Draw another label at each subsequent visible width until the end of the visible view or annotation
    while (x + labelWidth < max) {
      labelData.push(x);
      x += visWidth;
    }
  }

  return labelData;
}

interface AnnotationLabelData {
  /** What text should be displayed on the label */
  displayName: string;
  /** The x-position of the label, relative to the slice */
  labelData: Array<number>;
  /** Whether there is space inside the annotation for the entire annotation name */
  spaceInside: boolean;
  /** Whether the label is inside the annotation */
  inside: boolean;
}

/**
 * Formats the given annotation name to fit within the allotted space, ellipsizing if necessary.
 */
export function formatAnnotationDisplayName(name: string, nAvailableChars: number): string {
  if (nAvailableChars < name.length) {
    if (nAvailableChars < 5) {
      return '';
    }
    return ellipsize(name, nAvailableChars, EllipsisDirection.MIDDLE);
  }
  return name;
}

/**
 * Figures out how to draw labels, based on the constraints of the annotation and the slice.
 * @param params An AnnotationRenderParams object
 * @param type A Type object, the Type of the annotation label
 * @param residuesType A The Type object of the residues slice
 * @param annotationName The name of the annotation
 * @param annotationStrand The strand on which the annotation lies
 * @param layeringMode How to lay out the annotations within the slice
 * @param visibleWidth The width in pixels of the slice
 * @param leftBP The residue index of the LHS of the viewport on the minimap, if we're on the minimap
 * @param rightBP The residue index of the RHS of the viewport on the minimap, if we're on the minimap
 * @param inlineAnnotationLabelPlacement If there is not enough space for the label within the annotation, where to show it
 * @param inlineLabelPadding How much CSS padding in pixels to leave around the label text
 */
export function getAnnotationLabelData(
  params: AnnotationRenderParams,
  type: Type,
  residuesType: Type,
  annotationName: string,
  annotationStrand: Strand,
  layeringMode: LayeringMode,
  visibleWidth: number,
  leftBP: number | null,
  rightBP: number | null,
  inlineLabelPadding: number,
  inlineAnnotationLabelPlacement?: string
): AnnotationLabelData {
  const {width, tipWidth, arrowsOffset, arrowSpacing, arrowWidth, start} = params;
  const padding = arrowsOffset + arrowSpacing + arrowWidth;
  let displayName = params.forceName ?? annotationName;
  const labelWidth = type.width(displayName.length);
  const spaceInside = labelWidth + 2 * padding < width - tipWidth;
  let labelData: Array<number> = [];
  let inside = true;

  if (!spaceInside) {
    if (layeringMode === MapsConstants.LINEAR_LAYER_FULL) {
      const displayNameWidth = width - tipWidth - padding * 2;
      const nAvailableChars = displayNameWidth / type.charWidth();
      displayName = formatAnnotationDisplayName(displayName, nAvailableChars);
      labelData = getLabelLocations(
        displayNameWidth,
        tipWidth,
        width,
        annotationStrand,
        visibleWidth,
        residuesType,
        leftBP,
        rightBP,
        start
      );
    } else {
      inside = false;
      // Otherwise, we show the label on the outside.
      switch (inlineAnnotationLabelPlacement) {
        case 'after':
          labelData.push(width + inlineLabelPadding);
          break;
        case 'before':
          labelData.push(-inlineLabelPadding - labelWidth);
          break;
        default:
          break;
      }
    }
  } else {
    labelData = getLabelLocations(
      labelWidth,
      tipWidth,
      width,
      annotationStrand,
      visibleWidth,
      residuesType,
      leftBP,
      rightBP,
      start
    );
  }

  return {
    displayName,
    labelData,
    spaceInside,
    inside,
  };
}
